import { faArrowsRotate, faChevronRight, faTag } from '@fortawesome/pro-regular-svg-icons';
import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Image, Text, TextLink } from '@modusign/design-system';
import { fromBuffer } from 'file-type';
import { ReactElement, useRef, useState } from 'react';
import { useToggle } from 'react-use';

import batchUpdateImage from '@app/assets/external-document-batch-update.png';
import categoryManagementImage from '@app/assets/external-document-category-management.png';
import { WindowModal, WindowModalHeader } from '@app/components/Modal/WindowModal';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';

import DownloadBatchTemplateModal from '../DownloadBatchTemplateModal';
import ExcelFileExtensionErrorModal from '../ExcelFileExtensionErrorModal';
import UploadBatchTemplateModal from '../UploadBatchTemplateModal';

type TabType = 'category' | 'batchUpdate';
type ModalType = 'main' | 'upload' | 'download';

const tabs: Record<
  TabType,
  {
    name: string;
    icon: ReactElement;
    imageSrc: string;
    description: string;
  }
> = {
  category: {
    name: '계약 유형 분류',
    icon: <FontAwesomeIcon icon={faTag} style={{ width: '14px', color: '#757575' }} />,
    imageSrc: categoryManagementImage,
    description:
      '계약 유형별로 라벨을 적용해 보세요.\n다양한 유형의 계약을 쉽게 분류할 수 있습니다.',
  },
  batchUpdate: {
    name: '계약 정보 일괄 업데이트',
    icon: <FontAwesomeIcon icon={faArrowsRotate} style={{ width: '14px', color: '#757575' }} />,
    imageSrc: batchUpdateImage,
    description:
      '계약 정보를 일괄 업데이트할 수 있는 양식을 다운받습니다.\n정보 입력 후 엑셀을 업로드해 주세요.',
  },
};

interface DocumentManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DocumentManagementModal({ isOpen, onClose }: DocumentManagementModalProps) {
  const [modalType, setModalType] = useState<ModalType>('main');
  const [tab, setTab] = useState<TabType>('batchUpdate');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [isExcelFileTypeErrorModalOpen, toggleExcelFileTypeErrorModal] = useToggle(false);

  switch (modalType) {
    case 'main':
      return (
        <WindowModal isOpen={isOpen} maxWidth="md" onRequestClose={onClose}>
          <input
            accept=".xlsx"
            hidden
            ref={fileInputRef}
            type="file"
            onChange={async (e) => {
              if (!e.target.files?.[0]) {
                return;
              }

              const arrayBuffer = await e.target.files[0].arrayBuffer();

              const extension =
                (await fromBuffer(arrayBuffer))?.ext ??
                getExtensionFromFileName(e.target.files[0].name).slice(1);
              if (extension !== 'xlsx') {
                toggleExcelFileTypeErrorModal();
                return;
              }

              setExcelFile(e.target.files?.[0]);
              setModalType('upload');
            }}
          />
          <WindowModalHeader
            sx={{ color: 'textNeutralLight', fontWeight: 'bold' }}
            onClose={onClose}
          >
            <FontAwesomeIcon icon={faGear} style={{ marginRight: '4px' }} />
            계약 관리
          </WindowModalHeader>
          <Flex alignItems="stretch" width="100%">
            <Flex
              borderRight="1px solid"
              borderRightColor="#e1e1e1"
              flex="none"
              flexDirection="column"
              p={6}
              width="240px"
            >
              <Flex
                alignItems="stretch"
                as="ul"
                flex="auto"
                flexDirection="column"
                gap={3}
                role="tablist"
              >
                {(['batchUpdate', 'category'] as TabType[]).map((type) => {
                  const { icon, name } = tabs[type];
                  return (
                    <li key={type}>
                      <Text
                        aria-controls="DocumentManagementModal--tabpanel"
                        aria-selected={tab === type}
                        as="button"
                        fontWeight="medium"
                        role="tab"
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          border: '1px solid',
                          borderColor:
                            tab === type ? 'buttonBorderPrimaryBase' : 'buttonBorderNeutralBase',
                          borderRadius: '4px',
                          p: 6,
                          width: '100%',
                        }}
                        type="button"
                        onClick={() => setTab(type)}
                      >
                        {icon}
                        <Text fontWeight="medium" ml={2}>
                          {name}
                        </Text>
                      </Text>
                    </li>
                  );
                })}
              </Flex>
              <Box borderTop="1px solid" borderTopColor="dividerNeutralBase" pb="14px" pt={7}>
                <TextLink sx={{ textDecoration: 'none' }} to="/external-documents/batch-update">
                  계약 업데이트 내역 <FontAwesomeIcon icon={faChevronRight} />
                </TextLink>
              </Box>
            </Flex>
            <Flex
              alignItems="center"
              flex="auto"
              flexDirection="column"
              id="DocumentManagementModal--tabpanel"
              mx={7}
              my={6}
              pb={8}
              role="tabpanel"
            >
              <Image src={tabs[tab].imageSrc} sx={{ borderRadius: '8px' }} />
              <Text as="p" mt={7} textAlign="center" whiteSpace="pre">
                {tabs[tab].description}
              </Text>
              {tab === 'category' && (
                <TextLink
                  href="https://support.modusign.co.kr/114456043101?hs_preview=KCazstWU-114456043101#:~:text=%EB%B6%80%EC%97%AC%ED%95%98%EC%A7%80%20%EC%95%8A%EC%95%84%EC%95%BC%20%ED%95%A9%EB%8B%88%EB%8B%A4.-,%F0%9F%93%8C%20%EB%9D%BC%EB%B2%A8%20%EC%84%A4%EC%A0%95%ED%95%98%EA%B8%B0,-%E2%9C%94%20%EB%9D%BC%EB%B2%A8%EC%9D%80%20%EB%AC%B8%EC%84%9C%EB%A5%BC"
                  mt={4}
                  target="_blank"
                >
                  라벨 설정 방법
                </TextLink>
              )}
              {tab === 'batchUpdate' && (
                <Flex mt={7}>
                  <Button type="button" onClick={() => setModalType('download')}>
                    설정용 엑셀 다운로드
                  </Button>
                  <Button ml={3} type="button" onClick={() => fileInputRef.current?.click()}>
                    엑셀 업로드
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
          <ExcelFileExtensionErrorModal
            isOpen={isExcelFileTypeErrorModalOpen}
            onConfirm={toggleExcelFileTypeErrorModal}
          />
        </WindowModal>
      );
    case 'upload':
      return (
        <UploadBatchTemplateModal
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          file={excelFile!}
          isOpen
          setFile={setExcelFile}
          onClose={onClose}
        />
      );
    case 'download':
      return <DownloadBatchTemplateModal isOpen onClose={onClose} />;
    default:
      return null;
  }
}
